import { shopPosKeys } from "@/modules/merchant/presentation/api/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddWorkshopJobLaborDTO,
  AddWorkshopJobPartDTO,
  CreateWorkshopJobDTO,
  PatchWorkshopJobPartDTO,
  SetWorkshopJobStatusDTO,
  UpdateWorkshopJobDTO,
  WorkshopCheckoutDTO,
  WorkshopFitmentSkusQueryDTO,
  WorkshopJobListQueryDTO,
  WorkshopLaborPresetsQueryDTO,
} from "../../domain/contracts";
import { workshopApi } from "./client";

export const workshopKeys = {
  all: (shopId: string) => ["shops", shopId, "workshop"] as const,
  jobs: (shopId: string, q: WorkshopJobListQueryDTO) =>
    [...workshopKeys.all(shopId), "jobs", q] as const,
  job: (shopId: string, jobId: string) =>
    [...workshopKeys.all(shopId), "job", jobId] as const,
  makes: (shopId: string) => [...workshopKeys.all(shopId), "makes"] as const,
  models: (shopId: string, makeId: string) =>
    [...workshopKeys.all(shopId), "models", makeId] as const,
  configs: (shopId: string, modelId: string) =>
    [...workshopKeys.all(shopId), "configs", modelId] as const,
  config: (shopId: string, id: string) =>
    [...workshopKeys.all(shopId), "config", id] as const,
  fitmentSkus: (shopId: string, q: WorkshopFitmentSkusQueryDTO) =>
    [...workshopKeys.all(shopId), "fitmentSkus", q] as const,
};

function invalidateJob(
  qc: ReturnType<typeof useQueryClient>,
  shopId: string,
  jobId: string,
) {
  qc.invalidateQueries({ queryKey: workshopKeys.all(shopId) });
  qc.invalidateQueries({ queryKey: workshopKeys.job(shopId, jobId) });
}

export function useWorkshopJobsQuery(
  shopId: string | undefined,
  query: WorkshopJobListQueryDTO,
) {
  return useQuery({
    queryKey: workshopKeys.jobs(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.listJobs(shopId, query);
    },
    enabled: !!shopId,
  });
}

export function useWorkshopJobQuery(
  shopId: string | undefined,
  jobId: string | undefined,
) {
  return useQuery({
    queryKey: workshopKeys.job(shopId ?? "", jobId ?? ""),
    queryFn: () => {
      if (!shopId || !jobId) throw new Error("ids required");
      return workshopApi.getJob(shopId, jobId);
    },
    enabled: !!shopId && !!jobId,
  });
}

export function useCreateWorkshopJobMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWorkshopJobDTO) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.createJob(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: workshopKeys.all(shopId) });
      }
    },
  });
}

export function useUpdateWorkshopJobMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; body: UpdateWorkshopJobDTO }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.updateJob(shopId, args.jobId, args.body);
    },
    onSuccess: (_data, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useSetWorkshopJobStatusMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; body: SetWorkshopJobStatusDTO }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.setJobStatus(shopId, args.jobId, args.body);
    },
    onSuccess: (_data, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useWorkshopVehicleMakesQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: workshopKeys.makes(shopId ?? ""),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.listVehicleMakes(shopId);
    },
    enabled: !!shopId,
  });
}

export function useWorkshopVehicleModelsQuery(
  shopId: string | undefined,
  makeId: string | undefined,
) {
  return useQuery({
    queryKey: workshopKeys.models(shopId ?? "", makeId ?? ""),
    queryFn: () => {
      if (!shopId || !makeId) throw new Error("ids required");
      return workshopApi.listVehicleModels(shopId, makeId);
    },
    enabled: !!shopId && !!makeId,
  });
}

export function useWorkshopVehicleConfigurationsQuery(
  shopId: string | undefined,
  modelId: string | undefined,
) {
  return useQuery({
    queryKey: workshopKeys.configs(shopId ?? "", modelId ?? ""),
    queryFn: () => {
      if (!shopId || !modelId) throw new Error("ids required");
      return workshopApi.listVehicleConfigurations(shopId, modelId);
    },
    enabled: !!shopId && !!modelId,
  });
}

export function useWorkshopVehicleConfigurationQuery(
  shopId: string | undefined,
  configurationId: string | undefined | null,
) {
  return useQuery({
    queryKey: workshopKeys.config(shopId ?? "", configurationId ?? ""),
    queryFn: () => {
      if (!shopId || !configurationId) throw new Error("ids required");
      return workshopApi.getVehicleConfiguration(shopId, configurationId);
    },
    enabled: !!shopId && !!configurationId,
  });
}

export function useWorkshopFitmentSkusQuery(
  shopId: string | undefined,
  query: WorkshopFitmentSkusQueryDTO | undefined,
) {
  return useQuery({
    queryKey: workshopKeys.fitmentSkus(
      shopId ?? "",
      query ?? { branchId: "", limit: 50, fitmentOnly: false },
    ),
    queryFn: () => {
      if (!shopId || !query) throw new Error("required");
      return workshopApi.listFitmentSkus(shopId, query);
    },
    enabled: !!shopId && !!query?.branchId,
  });
}

export function useSetWorkshopVehicleConfigMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      jobId: string;
      vehicleConfigurationId: string | null;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.setVehicleConfiguration(
        shopId,
        args.jobId,
        args.vehicleConfigurationId,
      );
    },
    onSuccess: (_d, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useAddWorkshopJobPartMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; body: AddWorkshopJobPartDTO }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.addPart(shopId, args.jobId, args.body);
    },
    onSuccess: (_d, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function usePatchWorkshopJobPartMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      jobId: string;
      partId: string;
      body: PatchWorkshopJobPartDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.patchPart(
        shopId,
        args.jobId,
        args.partId,
        args.body,
      );
    },
    onSuccess: (_d, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useRemoveWorkshopJobPartMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; partId: string }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.removePart(shopId, args.jobId, args.partId);
    },
    onSuccess: (_d, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useWorkshopLaborPresetsQuery(
  shopId: string | undefined,
  query: Partial<WorkshopLaborPresetsQueryDTO>,
  enabled = true,
) {
  return useQuery({
    queryKey: [...workshopKeys.all(shopId ?? ""), "laborPresets", query],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.listLaborPresets(shopId, query);
    },
    enabled: !!shopId && enabled,
  });
}

export function useAddWorkshopJobLaborMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; body: AddWorkshopJobLaborDTO }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.addLabor(shopId, args.jobId, args.body);
    },
    onSuccess: (_d, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useRemoveWorkshopJobLaborMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; laborId: string }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.removeLabor(shopId, args.jobId, args.laborId);
    },
    onSuccess: (_d, vars) => {
      if (shopId) invalidateJob(qc, shopId, vars.jobId);
    },
  });
}

export function useWorkshopQuoteQuery(
  shopId: string | undefined,
  jobId: string | undefined,
) {
  return useQuery({
    queryKey: [...workshopKeys.all(shopId ?? ""), "quote", jobId ?? ""],
    queryFn: () => {
      if (!shopId || !jobId) throw new Error("ids required");
      return workshopApi.getQuote(shopId, jobId);
    },
    enabled: !!shopId && !!jobId,
  });
}

export function useApproveWorkshopQuoteMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.approveQuote(shopId, jobId);
    },
    onSuccess: (_d, jobId) => {
      if (shopId) invalidateJob(qc, shopId, jobId);
    },
  });
}

export function useWorkshopCheckoutMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      jobId: string;
      body: WorkshopCheckoutDTO;
      idempotencyKey: string;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return workshopApi.checkout(
        shopId,
        args.jobId,
        args.body,
        args.idempotencyKey,
      );
    },
    onSuccess: (_d, vars) => {
      if (shopId) {
        invalidateJob(qc, shopId, vars.jobId);
        // บิลเข้าประวัติ/รายงานขายเดิม
        void qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
    },
  });
}
